import { useCallback } from 'react'
import { useAppSelector, useAppDispatch } from 'store/store'
import * as actions from './actions'
import { teamMessageCleanUp, clearTeamError } from './slice'
import { TeamParams } from './types'

export const useTeam = () => {
  const dispatch = useAppDispatch()
  const teamState = useAppSelector(({ team }) => team)

  const getAllTeams = useCallback(() => {
    dispatch(actions.getAllTeams())
  }, [dispatch])

  const createTeam = useCallback(
    (payload: TeamParams) => {
      return dispatch(actions.createTeam(payload)).unwrap()
    },
    [dispatch],
  )

  const requestToJoinTeam = useCallback(
    (teamId: string, message: string) => {
      return dispatch(actions.requestToJoinTeam({ teamId, message })).unwrap()
    },
    [dispatch],
  )

  const getCaptainPanelData = useCallback(
    (teamId: string) => {
      return dispatch(actions.getCaptainPanelData(teamId))
    },
    [dispatch],
  )

  const getTeamDetails = useCallback(
    (teamId: string) => {
      return dispatch(actions.getTeamDetails(teamId))
    },
    [dispatch],
  )

  const handleTeamMember = useCallback(
    (teamId: string, userId: string, isJoined: boolean) => {
      return dispatch(actions.handleTeamMember({ teamId, userId, isJoined }))
    },
    [dispatch],
  )

  const followTeam = useCallback(
    (teamId: string) => {
      return dispatch(actions.followTeam(teamId))
    },
    [dispatch],
  )

  const updateTeam = useCallback(
    (teamId: string, team: TeamParams) => {
      return dispatch(actions.updateTeam({ teamId, team }))
    },
    [dispatch],
  )

  // Delete team
  const deleteTeam = useCallback(
    (teamId: string) => {
      return dispatch(actions.deleteTeam(teamId)).unwrap()
    },
    [dispatch],
  )

  // Admin: delete any team
  const adminDeleteTeam = useCallback(
    (teamId: string) => {
      return dispatch(actions.adminDeleteTeam(teamId)).unwrap()
    },
    [dispatch],
  )

  // Leave team
  const leaveTeam = useCallback(
    (teamId: string, options: { newOwnerId?: string; disband?: boolean }) => {
      return dispatch(actions.leaveTeam({ teamId, options })).unwrap()
    },
    [dispatch],
  )

  // Clear message
  const clearMessage = useCallback(() => {
    dispatch(teamMessageCleanUp())
  }, [dispatch])

  // Clear error
  const clearError = useCallback(() => {
    dispatch(clearTeamError())
  }, [dispatch])

  return {
    ...teamState,
    getAllTeams,
    createTeam,
    handleTeamMember,
    getCaptainPanelData,
    getTeamDetails,
    followTeam,
    updateTeam,
    requestToJoinTeam,
    deleteTeam,
    adminDeleteTeam,
    leaveTeam,
    clearMessage,
    clearError,
  }
}
